/**
 * Schema Validator — Frontend-side data structure validation.
 *
 * Verifies that data fetched from Firestore matches the expected shapes
 * defined in go-server/schema/feedback-result.schema.json.
 *
 * Usage:
 *   import { validatePersonaResult, validateSummary } from '@/lib/schema-validator';
 *   const errors = validatePersonaResult(data);
 *   if (errors.length > 0) console.warn('Data contract violation:', errors);
 */

export interface ValidationError {
  field: string;
  expected: string;
  actual: string;
}

function isObject(v: any): v is Record<string, any> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function validateRequired(data: any, field: string, type: string): ValidationError | null {
  if (data === undefined || data === null) {
    return { field, expected: type, actual: 'undefined' };
  }
  if (type === 'number' && typeof data !== 'number') {
    return { field, expected: type, actual: typeof data };
  }
  if (type === 'string' && typeof data !== 'string') {
    return { field, expected: type, actual: typeof data };
  }
  if (type === 'array' && !Array.isArray(data)) {
    return { field, expected: type, actual: typeof data };
  }
  if (type === 'object' && !isObject(data)) {
    return { field, expected: type, actual: typeof data };
  }
  return null;
}

const CATEGORY_RATING_FIELDS = ['rating', 'comment'];
const CATEGORY_RATING_TYPES: Record<string, string> = { rating: 'number', comment: 'string' };

function validateCategoryRating(data: any, prefix: string): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!isObject(data)) {
    errors.push({ field: prefix, expected: 'object { rating, comment }', actual: typeof data });
    return errors;
  }
  for (const field of CATEGORY_RATING_FIELDS) {
    const err = validateRequired(data[field], `${prefix}.${field}`, CATEGORY_RATING_TYPES[field]);
    if (err) errors.push(err);
  }
  return errors;
}

const INSIGHT_FIELDS = ['start', 'end', 'type', 'comment'];
const INSIGHT_TYPES: Record<string, string> = {
  start: 'number', end: 'number', type: 'string', comment: 'string',
};

function validateInsight(data: any, prefix: string): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!isObject(data)) {
    errors.push({ field: prefix, expected: 'object { start, end, type, comment }', actual: typeof data });
    return errors;
  }
  for (const field of INSIGHT_FIELDS) {
    const err = validateRequired(data[field], `${prefix}.${field}`, INSIGHT_TYPES[field]);
    if (err) errors.push(err);
  }
  return errors;
}

export function validatePersonaResult(data: any): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!isObject(data)) {
    errors.push({ field: '(root)', expected: 'object', actual: typeof data });
    return errors;
  }

  // Required top-level fields
  const requiredFields: Record<string, string> = {
    agent: 'string', personaId: 'string', personaName: 'string',
    comment: 'string', rating: 'number',
    insights: 'array', opinion: 'string', cost: 'number',
  };
  for (const [field, type] of Object.entries(requiredFields)) {
    const err = validateRequired(data[field], field, type);
    if (err) errors.push(err);
  }

  // Category ratings
  const categories = ['visual', 'content', 'script', 'sound_design', 'pacing', 'performance', 'target_focus'];
  for (const cat of categories) {
    errors.push(...validateCategoryRating(data[cat], cat));
  }

  // Insights array
  if (Array.isArray(data.insights)) {
    data.insights.forEach((insight: any, i: number) => {
      errors.push(...validateInsight(insight, `insights[${i}]`));
    });
  }

  return errors;
}

const TIMEBLOCK_FIELDS = ['block', 'start', 'end', 'score', 'summary'];
const TIMEBLOCK_TYPES: Record<string, string> = {
  block: 'number', start: 'number', end: 'number', score: 'number', summary: 'string',
};

function validateTimeBlock(data: any, prefix: string): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!isObject(data)) return errors;
  for (const field of TIMEBLOCK_FIELDS) {
    const err = validateRequired(data[field], `${prefix}.${field}`, TIMEBLOCK_TYPES[field]);
    if (err) errors.push(err);
  }
  if (Array.isArray(data.insights)) {
    data.insights.forEach((insight: any, i: number) => {
      errors.push(...validateInsight(insight, `${prefix}.insights[${i}]`));
    });
  }
  return errors;
}

const RATINGSUMMARY_FIELDS = ['type', 'min', 'max', 'avg', 'comment'];
const RATINGSUMMARY_TYPES: Record<string, string> = {
  type: 'string', min: 'number', max: 'number', avg: 'number', comment: 'string',
};

function validateRatingSummary(data: any, prefix: string): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!isObject(data)) return errors;
  for (const field of RATINGSUMMARY_FIELDS) {
    const err = validateRequired(data[field], `${prefix}.${field}`, RATINGSUMMARY_TYPES[field]);
    if (err) errors.push(err);
  }
  return errors;
}

export function validateSummary(data: any): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!isObject(data)) {
    errors.push({ field: '(root)', expected: 'object', actual: typeof data });
    return errors;
  }

  const requiredFields: Record<string, string> = {
    agent: 'string', insights: 'array', sense_analysis: 'array',
    ratings: 'array', cost: 'number',
  };
  for (const [field, type] of Object.entries(requiredFields)) {
    const err = validateRequired(data[field], field, type);
    if (err) errors.push(err);
  }

  if (Array.isArray(data.sense_analysis)) {
    data.sense_analysis.forEach((block: any, i: number) => {
      errors.push(...validateTimeBlock(block, `sense_analysis[${i}]`));
    });
  }

  if (Array.isArray(data.ratings)) {
    data.ratings.forEach((rating: any, i: number) => {
      errors.push(...validateRatingSummary(rating, `ratings[${i}]`));
    });
  }

  return errors;
}

/**
 * Logs validation errors as warnings with a consistent format.
 */
export function logValidationErrors(context: string, errors: ValidationError[]): void {
  if (errors.length === 0) return;
  console.warn(`[SCHEMA] ${context} — ${errors.length} data contract violation(s):`);
  for (const err of errors) {
    console.warn(`  [SCHEMA] ${err.field}: expected ${err.expected}, got ${err.actual}`);
  }
}
